import * as React from 'react';
import classNames from 'classnames';
import Measure from 'react-measure';
import {WithI18nProps, withI18n} from '@shopify/react-i18n';
import {Flex} from 'components/UIKit';
import {Filter, SortOption} from 'interfaces/ShopFilters';
import {SortUpdateFunction, FiltersUpdateFunction} from './types';
import {FilterSection, FilterSheet, FilterSort, Popover} from './components';
import * as locales from './locales';
import localStyles from './shop-filters.scss';
import {
  cloneFilters,
  getFilterLabel,
  resetFilterState,
  updateFilterState,
  filterHasSelection,
  filtersHaveSelection,
} from './utilities';

export interface ShopFiltersProps {
  sortBy: string;
  filters: Filter[];
  disabled: boolean;
  showSort?: boolean;
  sortOptions?: SortOption[];
  onSortChange?: SortUpdateFunction;
  onFilterUpdate?: FiltersUpdateFunction;
  onPopoverChange?: (filterType: string) => any;
}

export interface ShopFiltersState {
  sheetOpen: boolean;
  sheetFilters: Filter[];
  sheetSortBy: string;
  staticWidths?: StaticWidths;
  activePopover: string;
  popoverWidths: {
    [filterType: string]: number;
  };
  containerWidth: number;
  showFilterTypes: string[];
}

interface StaticWidths {
  resetAll?: number;
  moreFilters?: number;
}

interface FilterWidthPriority {
  type: string;
  width: number;
  hasSelection: boolean;
  isActive: boolean;
}

function NOOP() {}

const MARGIN_OF_ERROR = 50;

const style = {
  filterWrap: 'shop-filters',
  hidden: 'shop-filters--hidden',
  hideMobile: 'hide--mobile',

  moreFiltersHidden: 'shop-filters__more-filters--hidden',
  moreFiltersButton:
    'shop-filters__more-filters marketing-button marketing-button--skin-light marketing-button--small shop-filters__button ui-popover__container hide--mobile',

  mobileFiltersButton:
    'marketing-button marketing-button--skin-light marketing-button--small shop-filters__button hide--desktop hide--tablet',

  resetAllButton: 'shop-filters__reset-filters body-link ui-popover__container',
  resetAllButtonHidden: 'shop-filters__reset-filters--hidden',
};

/**
 * Given two filter width and priority descriptors, return a sort value
 * @param optA Option A
 * @param optB Option B
 */
function sortByWidthAndPriorty(optA: FilterWidthPriority, optB: FilterWidthPriority) {
  if (optA.isActive && optB.isActive) {
    return 0;
  }

  if (optA.isActive || (optA.hasSelection && !optB.hasSelection)) {
    return -1;
  }
  if (optB.isActive || (!optA.hasSelection && optB.hasSelection)) {
    return 1;
  }

  return optA.width - optB.width;
}

/**
 * Given a DOM element, return its width including margins
 * @param el The DOM element
 */
function getWidthWithMargin(el) {
  const computed = window.getComputedStyle(el);
  const marginLeft = computed.marginLeft || '0';
  const marginRight = computed.marginRight || '0';
  return el.clientWidth + parseInt(marginRight, 10) + parseInt(marginLeft, 10);
}

class ShopFiltersComponent extends React.Component<
  ShopFiltersProps & WithI18nProps,
  ShopFiltersState
> {
  resetAllRef: any;
  moreFiltersRef: any;

  constructor(props) {
    super(props);

    this.state = {
      activePopover: '',
      containerWidth: 0,
      popoverWidths: {},
      showFilterTypes: props.filters.map((filter) => filter.type),
      staticWidths: {
        resetAll: 0,
        moreFilters: 0,
      },
      sheetOpen: false,
      sheetFilters: cloneFilters(props.filters),
      sheetSortBy: props.sortBy,
    };
  }

  // Set refs
  handleResetAllRef = (ref) => {
    this.resetAllRef = ref;
  };

  handleMoreFiltersRef = (ref) => {
    this.moreFiltersRef = ref;
  };

  /**
   * Handle resize event from `react-measure` for filter container.
   * Update the state value and trigger a showing filter update.
   * @param contentRect The ContentRect descriptor returned from `Measure`
   */
  handleContainerResize = (contentRect) => {
    this.setState(
      {
        containerWidth: contentRect.bounds.width,
      },
      this.updateShowingFilters,
    );
  };

  /**
   * Handle resize event from `Popover`, update corresponding `popoverWidth` key.
   * @param id The Fiter type passed to `Popover`
   * @param contentRect The ContentRect descriptor returned from `Measure`
   */
  handlePopoverResize = (id, width) => {
    this.setState((prevState) => {
      const newWidths = {...prevState.popoverWidths};

      if (width === false) {
        delete newWidths[id];
      } else {
        newWidths[id] = width;
      }

      return {...prevState, popoverWidths: newWidths};
    }, this.updateShowingFilters);
  };

  /**
   * Handle `More filters` button click.
   * Should show sheet and initialize sheet filter state
   */
  handleMoreFiltersClick = () => {
    const {filters, sortBy} = this.props;
    this.setState({
      sheetOpen: true,
      sheetFilters: cloneFilters(filters),
      sheetSortBy: sortBy,
    });
  };

  /**
   * Handle popover should close event.
   * Should clear active popover, and dispatch `onPopoverClose`
   */
  handlePopoverClose = () => {
    const {onPopoverChange = NOOP} = this.props;
    this.setState({activePopover: ''}, () => onPopoverChange(''));
  };

  /**
   * Handle popover should open event.
   * Should set active popover, and dispatch to `onPopoverOpen`
   * @param label The active popover id
   */
  handlePopoverOpen = (id) => {
    const {onPopoverChange = NOOP} = this.props;
    this.setState({activePopover: id}, () => onPopoverChange(id));
  };

  /**
   * Handle filter selection updates
   * Should proxy update to `onFilterUpdate`, or store intermediary state for sheet.
   * @param filterType The filter type to update
   * @param optionId The optionId to update
   * @param value The updated value
   */
  handleFilterUpdate = (filterType, optionId, value): any => {
    const {filters, onFilterUpdate = NOOP} = this.props;
    const {sheetOpen} = this.state;

    if (sheetOpen) {
      this.setState((prevState) => ({
        ...prevState,
        sheetFilters: updateFilterState(prevState.sheetFilters, filterType, optionId, value),
      }));
      return;
    }

    const filterState = updateFilterState(filters, filterType, optionId, value);
    onFilterUpdate(filterState);
  };

  /**
   * Handle filter reset updates.
   * Should proxy to `onFilterUpdate` or store intermediary state for sheet
   * @param type Optionally specify filter type to reset, otherwise reset all filters
   */
  handleReset = (type?: string): any => {
    const {filters, onFilterUpdate = NOOP} = this.props;
    const {sheetOpen} = this.state;

    if (sheetOpen) {
      this.setState((prevState) => ({
        ...prevState,
        sheetFilters: resetFilterState(prevState.sheetFilters, type),
      }));
      return;
    }

    const filterState = resetFilterState(filters, type);
    onFilterUpdate(filterState);
  };

  /**
   * Handle sheet apply click.
   * Should proxy intermediary state changes to `onFilterUpdate`, and `onSortChange`
   */
  handleSheetApplyClick = () => {
    const {onSortChange = NOOP, onFilterUpdate = NOOP, sortBy} = this.props;
    const {sheetFilters, sheetSortBy} = this.state;

    if (sortBy !== sheetSortBy) {
      onSortChange(sheetSortBy);
    }

    onFilterUpdate(sheetFilters);

    this.setState({sheetOpen: false});
  };

  /**
   * Handle change event from sort drop down.
   * Should store in intermediary state if sheet is open.
   * @param event React onChange event proxy
   */
  handleSortChange = (event) => {
    const {onSortChange = NOOP} = this.props;
    const {sheetOpen} = this.state;

    if (sheetOpen) {
      return this.setState({sheetSortBy: event.target.value});
    }

    return onSortChange(event.target.value);
  };

  /**
   * Handle reset all button click
   */
  handleResetAllClick = () => this.handleReset();

  /**
   * Handle sheet close button click
   */
  handleSheetCloseClick = () => {
    this.setState({sheetOpen: false});
  };

  /**
   * Does this component have hidden filters?
   */
  hasHiddenFilters = (): boolean => {
    const {filters} = this.props;
    const {showFilterTypes} = this.state;

    return filters.length !== showFilterTypes.length;
  };

  /**
   * Given the current selection state, `containerWidth`, `popoverWidths`, and `staticWidths`: calculate which filters, if any, should be hidden
   */
  updateShowingFilters = () => {
    const {filters} = this.props;

    this.setState((prevState) => {
      const {staticWidths = {}, activePopover, popoverWidths, containerWidth} = prevState;

      const resetAll = staticWidths.resetAll || getWidthWithMargin(this.resetAllRef);
      const moreFilters = staticWidths.moreFilters || getWidthWithMargin(this.moreFiltersRef);

      let maxWidth = containerWidth - MARGIN_OF_ERROR;
      if (filtersHaveSelection(filters)) {
        maxWidth -= resetAll;
      }

      const filtersByWidthAndState: FilterWidthPriority[] = filters
        .filter((filter) => popoverWidths[filter.type])
        .map((filter) => ({
          type: filter.type,
          width: popoverWidths[filter.type],
          isActive: activePopover === filter.type,
          hasSelection: filterHasSelection(filter),
        }))
        .sort(sortByWidthAndPriorty);

      let totalWidth = 0;
      const showFilters: FilterWidthPriority[] = [];
      for (const filter of filtersByWidthAndState) {
        const adjustedWidth = totalWidth + filter.width;
        if (adjustedWidth > maxWidth) {
          break;
        }
        showFilters.push(filter);
        totalWidth += filter.width;
      }

      // If we need to show More Filters button, make sure there is room, otherwise make it.
      let adjustedWidth = totalWidth + moreFilters;
      const hasHidden = showFilters.length !== filters.length;
      if (hasHidden && adjustedWidth > maxWidth) {
        while (adjustedWidth > maxWidth && showFilters.length) {
          const removed = showFilters.pop();
          if (removed) {
            adjustedWidth -= removed.width;
          }
        }
      }

      const showFilterTypes: string[] = showFilters.map((filter) => filter.type);

      return {
        ...prevState,
        showFilterTypes,
        staticWidths: {resetAll, moreFilters},
      };
    });
  };

  render() {
    const {i18n, filters, sortBy, disabled, showSort, sortOptions} = this.props;

    const {sheetOpen, sheetSortBy, sheetFilters, activePopover, showFilterTypes} = this.state;

    const showResetAll = filtersHaveSelection(filters);
    const showMoreFilters = this.hasHiddenFilters();

    return (
      <>
        <FilterSheet
          active={!disabled}
          sortBy={sheetSortBy}
          open={sheetOpen}
          onApply={this.handleSheetApplyClick}
          onResetAll={this.handleResetAllClick}
          sortOptions={sortOptions}
          onCloseClick={this.handleSheetCloseClick}
          onSortChange={this.handleSortChange}
          showResetAll={showResetAll}
        >
          {sheetFilters.map((filter) => (
            <FilterSection
              key={`sheet.${filter.type}`}
              filter={filter}
              onReset={this.handleReset}
              bordered={false}
              namespace="sheet"
              onInputChange={this.handleFilterUpdate}
            />
          ))}
        </FilterSheet>
        <Flex flex="1" justifyContent="space-between">
          <Measure bounds onResize={this.handleContainerResize}>
            {({measureRef}) => (
              <div ref={measureRef} className={style.filterWrap}>
                {filters.map((filter) => (
                  <Popover
                    id={filter.type}
                    key={filter.type}
                    open={filter.type === activePopover}
                    label={getFilterLabel(filter, i18n)}
                    enabled={!disabled}
                    onResize={this.handlePopoverResize}
                    className={classNames(style.hideMobile, {
                      [style.hidden]: !showFilterTypes.includes(filter.type),
                    })}
                    onButtonClick={this.handlePopoverOpen}
                    onShouldOpen={this.handlePopoverOpen}
                    onShouldClose={this.handlePopoverClose}
                    forceActiveState={Boolean(filterHasSelection(filter))}
                  >
                    <FilterSection
                      filter={filter}
                      bordered={false}
                      onReset={this.handleReset}
                      onInputChange={this.handleFilterUpdate}
                    />
                  </Popover>
                ))}
                <button
                  ref={this.handleMoreFiltersRef}
                  type="button"
                  onClick={this.handleMoreFiltersClick}
                  className={style.mobileFiltersButton}
                >
                  {i18n.translate('mobileFilterCTA')}
                </button>
                <button
                  ref={this.handleMoreFiltersRef}
                  type="button"
                  onClick={this.handleMoreFiltersClick}
                  className={classNames(style.moreFiltersButton, {
                    [style.hidden]: !showMoreFilters,
                  })}
                >
                  {i18n.translate('moreFiltersCTA')}
                </button>
                <button
                  type="button"
                  ref={this.handleResetAllRef}
                  className={classNames(style.resetAllButton, {
                    [style.hidden]: !showResetAll,
                  })}
                  onClick={this.handleResetAllClick}
                >
                  {i18n.translate('resetAllFiltersCTA')}
                </button>
              </div>
            )}
          </Measure>
          {showSort && (
            <Flex flex="0 0 250px" className={localStyles.sortWrap}>
              <FilterSort
                active
                forSheet={false}
                selected={sortBy}
                sortOptions={sortOptions}
                onSelectChange={this.handleSortChange}
              />
            </Flex>
          )}
        </Flex>
      </>
    );
  }
}

export const ShopFilters = withI18n({
  id: 'ShopFilters',
  fallback: locales.en,
})(ShopFiltersComponent);
